import React, { useState, useRef, useEffect } from "react";
import { useSettings } from "../contexts/SettingsContext";

const DropdownBox = ({
  label,
  value,
  options = [],
  placeholder = "Select option",
  onChange,
  error,
  helperText,
  disabled = false,
  required = false,
  className = "",
  id,
  name,
  dropUpRight = false,
}) => {
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || "#3B5BDB";

  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectId = id || name || label?.toLowerCase().replace(/\s+/g, "-");

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`w-full ${className}`}
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm mb-2 text-slate-600"
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {/* Input Box */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`
          relative w-full h-12 px-4 pr-10 text-left rounded-xl border
          bg-gray-50 dark:bg-gray-800
          transition-all duration-200
          ${error ? "border-red-500" : "border-gray-200 dark:border-gray-600"}
          ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-gray-300"}
          whitespace-nowrap overflow-hidden text-ellipsis
        `}
        style={
          open && !error
            ? {
                border: "1px solid transparent",
                backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(135deg, ${primaryColor} 25%, #20C997 75%)`,
                backgroundOrigin: "border-box",
                backgroundClip: "padding-box, border-box",
              }
            : {}
        }
      >
        <span className={value ? "text-gray-800 dark:text-gray-100" : "text-gray-400"} style={{display:'block', width:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
          {value || placeholder}
        </span>

        {/* Arrow */}
        <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <span
            style={{
              display: "inline-block",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: `6px solid ${open ? primaryColor : "#9CA3AF"}`,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </span>
      </button>

      {/* Dropdown Options */}
      {open && !disabled && (
        <div className="relative">
          <ul
            className={
              `absolute z-20 w-full max-h-60 overflow-auto rounded-xl border bg-white dark:bg-gray-800 shadow-lg ` +
              (dropUpRight
                ? ' right-0 bottom-full origin-bottom-right'
                : ' mt-2 left-0 origin-top-left')
            }
            style={dropUpRight ? {marginBottom: '3.5rem'} : {}}
          >
            {options.map((option, idx) => (
              <li
                key={idx}
                onClick={() => {
                  onChange?.(option);
                  setOpen(false);
                }}
                className={`
                  px-4 py-2 cursor-pointer
                  transition-colors
                  ${value === option ? "bg-gray-100 dark:bg-gray-700 font-medium" : ""}
                  hover:bg-gray-100 dark:hover:bg-gray-700
                  break-words whitespace-normal max-w-full
                `}
                style={{wordBreak:'break-word', whiteSpace:'normal', maxWidth:'100%'}}
              >
                <span style={{display:'block', width:'100%', overflowWrap:'break-word', wordBreak:'break-word'}}>{option}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(error || helperText) && (
        <p
          className={`mt-1.5 text-sm ${
            error ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
};

export default DropdownBox;
